import {
  Component,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { MainHeadingSubtextComponent } from '../../atoms/main-heading-subtext/main-heading-subtext.component';
import { MainHeadingComponent } from '../../atoms/main-heading/main-heading.component';
import { FormFieldComponent } from '../../atoms/form-field/form-field.component';
import { TextAreaComponent } from '../../atoms/text-area/text-area.component';
import { FilledButtonComponent } from '../../atoms/filled-button/filled-button.component';
import { StrokedButtonComponent } from '../../atoms/stroked-button/stroked-button.component';
import { DateFieldComponent } from '../../atoms/date-field/date-field.component';
import { ImgGridComponent } from '../../molecules/img-grid/img-grid.component';
import { EmailComponent } from '../../molecules/email/email.component';
import { ReactiveFormsModule } from '@angular/forms';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import {
  SendoutDateLock,
  SendoutService,
} from '../../../services/sendout/sendout.service';
import {
  GlobalDataService,
  SessionData,
} from '../../../services/global-data/global-data.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import {
  CreateSendout,
  Options,
  Sendout,
  SendoutStatus,
  SendoutStatusEnum,
  SendoutType,
  Store,
  Template,
  UpdateSendout,
  UserType,
} from '../../../types';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TemplateService } from '../../../services/template/template.service';
import { DialogService } from '../../../services/dialog/dialog.service';
import { SelectFieldComponent } from '../../atoms/select-field/select-field.component';
import {
  MailPreviewData,
  MailPreviewService,
} from '../../../services/mail-preview/mail-preview.service';
import { ctaUrlValidator } from '../../../validation/cta-url.validator';
import { firstValueFrom, map, Observable, Subscription } from 'rxjs';
import { Event } from '../../../types';
import { EventsService } from '../../../services/events/events.service';
import {
  addDays,
  format,
  formatISO,
  isBefore,
  isSunday,
  parseISO,
  startOfDay,
} from 'date-fns';
import { SkeletonComponent } from '../../molecules/skeleton/skeleton.component';
import { StoreService } from '../../../services/store/store.service';
import { LinkFormFieldComponent } from '../../atoms/link-form-field/link-form-field.component';
import { RteFormFieldComponent } from '../../atoms/rte-form-field/rte-form-field.component';
import { SendoutCommentsService } from '../../../services/sendout-comments/sendout-comments.service';
import { SendoutCommentsComponent } from '../../molecules/sendout-comments/sendout-comments.component';
import { RejectionDialogComponent } from '../../molecules/rejection-dialog/rejection-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { StorePickerDialogComponent } from '../../molecules/store-picker-dialog/store-picker-dialog.component';
import { CalendarDatePickerFieldComponent } from '../../molecules/calendar-date-picker-field/calendar-date-picker-field.component';
enum SendoutState {
  New = 'NEW',
  Existing = 'EXISTING',
}

@Component({
  selector: 'app-edit-multi-store-sendout',
  standalone: true,
  imports: [
    RouterLink,
    CommonModule,
    MainHeadingSubtextComponent,
    MainHeadingComponent,
    FormFieldComponent,
    FilledButtonComponent,
    StrokedButtonComponent,
    DateFieldComponent,
    ImgGridComponent,
    EmailComponent,
    ReactiveFormsModule,
    SkeletonComponent,
    LinkFormFieldComponent,
    RteFormFieldComponent,
    SendoutCommentsComponent,
    MatSelectModule,
  ],
  templateUrl: './edit-multi-store-sendout.component.html',
  styleUrl: './edit-multi-store-sendout.component.css',
})
export class EditMultiStoreSendoutComponent implements OnInit, OnDestroy {
  // export enums for views
  UserType = UserType;
  SendoutState = SendoutState;
  loadError: string | null = null;
  private routeSubscription?: Subscription;
  SendoutStatus = SendoutStatus;

  sessionDataSubscription?: Subscription;
  sessionData!: SessionData;

  sendoutState: SendoutState = SendoutState.New;

  sendoutForm: FormGroup;
  emailForm: FormGroup;

  sendout!: Sendout;
  template: Template | null = null;

  minSendoutDate: Date;
  isLoading: boolean = true;

  statusOptions: Options[] = [
    { value: 'afventer', viewValue: 'Afventer' },
    { value: 'godkendt', viewValue: 'Godkendt' },
    { value: 'udsendt', viewValue: 'Udsendt' },
  ];

  sendoutDateLocks: SendoutDateLock[] = [];

  useEventUrlControl = new FormControl(false);

  stores: Store[] = [];

  selectedStores: Store[] = [];

  previewStoreSignature: string = 'Vejnavn 1, By';

  previewPoints: string = '1.234';

  readonly dialog = inject(MatDialog);

  constructor(
    private sendoutService: SendoutService,
    private globalDataService: GlobalDataService,
    private router: Router,
    private snackbarService: SnackbarService,
    private templateService: TemplateService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private mailPreviewService: MailPreviewService,
    private activatedRoute: ActivatedRoute,
    private eventService: EventsService,
    private storeService: StoreService,
    private sendoutCommentsService: SendoutCommentsService
  ) {
    this.minSendoutDate = addDays(startOfDay(new Date()), 3);
    if (isSunday(this.minSendoutDate)) {
      this.minSendoutDate = addDays(this.minSendoutDate, 1);
    }

    this.sendoutForm = new FormGroup({}); // Actual form initialization needs to happen after the template has been loaded

    this.emailForm = new FormGroup({
      email: new FormControl('', [Validators.email, Validators.required]),
    });
  }
  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this.sessionDataSubscription = this.globalDataService
      .getSessionData()
      .subscribe(async (sessionData) => {
        this.sessionData = sessionData;
      });

    this.stores = await firstValueFrom(this.storeService.getAllStores());

    this.routeSubscription = this.activatedRoute.url.subscribe(async () => {
      if (this.activatedRoute.snapshot.params['sendoutId']) {
        this.sendoutState = SendoutState.Existing;
        const sendoutId = parseInt(
          this.activatedRoute.snapshot.params['sendoutId'],
          10
        );
        await this.loadDataForSendoutId(sendoutId);
      } else {
        this.sendoutState = SendoutState.New;
        this.isLoading = true;
        this.loadError = null;
        const templateId =
          this.activatedRoute.snapshot.queryParams['templateId'];

        try {
          const template = await firstValueFrom(
            this.templateService.getTemplateById(templateId)
          );
          if (this.routeSubscription?.closed) return;
          this.template = template;
        } catch {
          if (this.routeSubscription?.closed) return;
          this.loadError = 'Skabelonen kunne ikke indlæses. Vælg en skabelon igen.';
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
          return;
        }
        this.initializeSendoutForm();
        let sendoutDate = this.activatedRoute.snapshot.queryParams[
          'sendoutDate'
        ]
          ? parseISO(this.activatedRoute.snapshot.queryParams['sendoutDate'])
          : this.minSendoutDate;

        this.sendout = this.templateService.createSendoutFromTemplate(
          sendoutDate,
          this.template,
          SendoutType.MultiStore,
          null
        );

        this.setFormValues({ ...this.sendout });
        this.sendoutForm.enable();
        this.sendoutForm.markAllAsTouched();
        this.sendoutForm.markAsDirty();
        this.sendoutForm.updateValueAndValidity();
        this.setPreviewData();
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }
  ngOnDestroy(): void {
    this.sessionDataSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
  }
  initializeSendoutForm() {
    if (!this.template) {
      throw new Error('Template is not loaded');
    }
    this.sendoutForm = new FormGroup({
      sendoutDate: new FormControl('', [Validators.required]),
      contactPerson: new FormControl('', []),
      sendoutName: new FormControl('', [Validators.required]),
      storeIds: new FormControl([], [Validators.required]),
      eventName: new FormControl(
        '',
        this.template.eventRequired === true ? [Validators.required] : null
      ),

      contentSubjectLine: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideSubjectLine ? false : true,
        },
        [Validators.required]
      ),
      contentPreheader: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverridePreheader ? false : true,
        },
        [Validators.required]
      ),
      contentHeadline: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideHeadline ? false : true,
        },
        [Validators.required, Validators.minLength(3)]
      ),
      contentBodyCopy: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideBodyCopy ? false : true,
        },
        [Validators.required]
      ),
      contentCtaUrl: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideCtaUrl ? false : true,
        },
        [ctaUrlValidator]
      ),
      contentImageUrl: new FormControl('', [Validators.required]),
      contentCtaText: new FormControl({
        value: '',
        disabled: this.template.allowOverrideCtaText ? false : true,
      }),
      contentDisclaimer: new FormControl(
        {
          value: '',
          disabled: this.template.allowOverrideDisclaimer ? false : true,
        },
        []
      ),
    });

    this.sendoutForm.get('contentCtaUrl')?.valueChanges.subscribe(() => {
      const ctaTextControl = this.sendoutForm.get('contentCtaText');
      if (ctaTextControl?.hasError('required') && !this.hasEffectiveCtaUrl()) {
        const { required, ...otherErrors } = ctaTextControl.errors!;
        ctaTextControl.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
      }
    });

    this.sendoutForm.valueChanges.subscribe((values) => {
      this.changeDetectorRef.detectChanges();
    });

  }

  async loadDataForSendoutId(id: number) {
    this.isLoading = true;
    this.changeDetectorRef.detectChanges();
    // Load template, sendout and event data
    this.sendout = await firstValueFrom(this.sendoutService.getSendoutById(id));
    if (!this.sendout) {
      throw new Error(`Sendout with id ${id} not found`);
    }
    this.template = await firstValueFrom(
      this.templateService.getTemplateById(this.sendout.sendoutTemplateId, this.sendout.id)
    );

    this.initializeSendoutForm();
    this.setFormValues({ ...this.sendout });

    // Set stores
    this.selectedStores = await firstValueFrom(
      this.storeService.resolveExistingStores(this.sendout.storeIds)
    );

    this.sendoutForm.markAsPristine();
    this.sendoutForm.enable();

    if (this.sendout.status !== SendoutStatus.Draft) {
      // Non-draft sendouts are read-only
      this.sendoutForm.disable();
    }

    this.setPreviewData();
    this.isLoading = false;
    this.changeDetectorRef.detectChanges();
  }

  setPreviewData() {
    const intFormat = new Intl.NumberFormat('da-DK', {
      style: 'decimal',
      maximumFractionDigits: 0,
    });
    this.previewPoints = intFormat.format(parseInt(format(new Date(), 'Hmm')));
  }

  dateBeforeValidator(referenceDate: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // If the control is empty, let other validators handle it (like required validator)
      }

      const inputDate = new Date(control.value);

      // Allow dates before the startDate
      if (isBefore(inputDate, referenceDate)) {
        return null; // Date is valid
      }
      return { dateBefore: true }; // Date is not valid
    };
  }
  dateBeforeNoEventValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const inputDate = new Date(control.value);
      const currDate = startOfDay(new Date());

      if (inputDate > currDate) {
        return null;
      }
      return { dateBefore: true };
    };
  }

  /**
   *
   * @param newStatus
   * @param forceSave  - if true, the form will be saved even if we're not saving a draft
   * @returns
   */
  onSendoutFormSubmit(
    newStatus: SendoutStatusEnum,
    forceSave: boolean = false,
    confirmedWithoutCta: boolean = false
  ) {
    // Check form validity - invalid forms may be saved to draft
    if (this.sendoutForm.invalid && newStatus !== SendoutStatus.Draft) {
      this.sendoutForm.markAllAsTouched();
      this.sendoutForm.updateValueAndValidity();
      return;
    }

    if (
      newStatus === SendoutStatus.AwaitingApproval &&
      this.hasEffectiveCtaUrl() &&
      !this.hasCtaText()
    ) {
      const ctaTextControl = this.sendoutForm.get('contentCtaText');
      ctaTextControl?.setErrors({ required: true });
      ctaTextControl?.markAsTouched();
      return;
    }

    if (
      newStatus === SendoutStatus.AwaitingApproval &&
      !confirmedWithoutCta &&
      this.hasCtaTextWithoutUrl()
    ) {
      this.dialogService.openDialog(
        'Fortsæt uden knap',
        'Events fra bookingsystemet kræver et link. Er du sikker på, at du ikke vil indsætte et link?',
        'Fortsæt uden knap',
        () => this.onSendoutFormSubmit(newStatus, forceSave, true),
        undefined,
        '500px',
        undefined,
        undefined,
        undefined,
        undefined,
        'Tilbage'
      );
      return;
    }

    if (this.sendoutState === SendoutState.New) {
      const reviewMetadata = this.getReviewMetadata(newStatus, true);
      const payload: CreateSendout = {
        sendoutType: this.sendout?.sendoutType,
        sendoutTemplateId: this.sendout?.sendoutTemplateId,
        storeIds: this.sendoutForm.value.storeIds,
        createdBy: this.sessionData.userId,
        updatedBy: this.sessionData.userId,
        sendoutName:
          this.sendoutForm.value.sendoutName === undefined
            ? this.sendout.sendoutName
            : this.sendoutForm.value.sendoutName,
        status: newStatus,
        eventId: this.sendout?.eventId || null,
        reviewedBy: reviewMetadata.reviewedBy,
        reviewedAt: reviewMetadata.reviewedAt,

        contentSubjectLine:
          this.sendoutForm.value.contentSubjectLine === undefined
            ? this.sendout.contentSubjectLine
            : this.sendoutForm.value.contentSubjectLine,
        contentPreheader: this.sendout?.contentPreheader,
        contentHeadline:
          this.sendoutForm.value.contentHeadline === undefined
            ? this.sendout.contentHeadline
            : this.sendoutForm.value.contentHeadline,
        contentBodyCopy:
          this.sendoutForm.value.contentBodyCopy === undefined
            ? this.sendout.contentBodyCopy
            : this.sendoutForm.value.contentBodyCopy,
        contentCtaText:
          this.sendoutForm.value.contentCtaText === undefined
            ? this.sendout.contentCtaText
            : this.sendoutForm.value.contentCtaText,
        contentCtaUrl:
          this.sendoutForm.value.contentCtaUrl === undefined
            ? this.sendout.contentCtaUrl
            : this.sendoutForm.value.contentCtaUrl,
        contentImageUrl:
          this.sendoutForm.value.contentImageUrl === undefined
            ? this.sendout.contentImageUrl
            : this.sendoutForm.value.contentImageUrl,
        contentDisclaimer:
          this.sendoutForm.value.contentDisclaimer === undefined
            ? this.sendout.contentDisclaimer
            : this.sendoutForm.value.contentDisclaimer,
        contactPerson:
          (this.sendoutForm.value.contactPerson === undefined
            ? this.sendout.contactPerson
            : this.sendoutForm.value.contactPerson) || '',
        sendoutDate: formatISO(new Date(this.sendoutForm.value.sendoutDate), {
          representation: 'date',
        }),
      };
      return this.executeCreateSendout(payload);
    }

    if (newStatus === SendoutStatus.Draft || forceSave) {
      const reviewMetadata = this.getReviewMetadata(
        newStatus,
        this.sendout?.status !== newStatus
      );
      const payload: UpdateSendout = {
        sendoutType: this.sendout?.sendoutType,
        sendoutTemplateId: this.sendout?.sendoutTemplateId,
        storeIds: this.sendoutForm.value.storeIds,
        updatedBy: this.sessionData.userId,
        // updatedAt: new Date(),
        sendoutName:
          this.sendoutForm.value.sendoutName === undefined
            ? this.sendout.sendoutName
            : this.sendoutForm.value.sendoutName,
        status: newStatus,
        eventId: this.sendout?.eventId,
        reviewedBy: reviewMetadata.reviewedBy,
        reviewedAt: reviewMetadata.reviewedAt,
        contentSubjectLine:
          this.sendoutForm.value.contentSubjectLine === undefined
            ? this.sendout.contentSubjectLine
            : this.sendoutForm.value.contentSubjectLine,
        contentPreheader: this.sendout?.contentPreheader,
        contentHeadline:
          this.sendoutForm.value.contentHeadline === undefined
            ? this.sendout.contentHeadline
            : this.sendoutForm.value.contentHeadline,
        contentBodyCopy:
          this.sendoutForm.value.contentBodyCopy === undefined
            ? this.sendout.contentBodyCopy
            : this.sendoutForm.value.contentBodyCopy,
        contentCtaText:
          this.sendoutForm.value.contentCtaText === undefined
            ? this.sendout.contentCtaText
            : this.sendoutForm.value.contentCtaText,
        contentCtaUrl:
          this.sendoutForm.value.contentCtaUrl === undefined
            ? this.sendout.contentCtaUrl
            : this.sendoutForm.value.contentCtaUrl,
        contentImageUrl:
          this.sendoutForm.value.contentImageUrl === undefined
            ? this.sendout.contentImageUrl
            : this.sendoutForm.value.contentImageUrl,
        contentDisclaimer:
          this.sendoutForm.value.contentDisclaimer === undefined
            ? this.sendout.contentDisclaimer
            : this.sendoutForm.value.contentDisclaimer,
        contactPerson:
          (this.sendoutForm.value.contactPerson === undefined
            ? this.sendout.contactPerson
            : this.sendoutForm.value.contactPerson) || '',
        sendoutDate: formatISO(new Date(this.sendoutForm.value.sendoutDate), {
          representation: 'date',
        }),
        id: this.sendout?.id,
      };
      return this.executeUpdateSendout(payload);
    } else {
      return this.updateSendoutStatus(newStatus);
    }
  }

  private hasEffectiveCtaUrl(): boolean {
    const ctaUrl = (this.sendoutForm.get('contentCtaUrl')?.value || '')
      .toString()
      .trim();

    return ctaUrl !== '' && ctaUrl !== '{{event URL}}';
  }

  private hasCtaTextWithoutUrl(): boolean {
    return this.hasCtaText() && !this.hasEffectiveCtaUrl();
  }

  private hasCtaText(): boolean {
    return !!this.sendoutForm
      .get('contentCtaText')
      ?.value?.toString()
      .trim();
  }

  async sendProofEmail() {
    if (
      this.emailForm.valid &&
      this.sendoutForm.valid &&
      this.sendout &&
      this.template
    ) {
      // Assemble preview data
      const data: MailPreviewData = {
        templateName: this.template.templateName,
        data: {
          CONTENT_BODY_COPY: this.sendoutForm.get('contentBodyCopy')?.value,
          CONTENT_CTA_TEXT: this.sendoutForm.get('contentCtaText')?.value,
          CONTENT_CTA_URL: this.sendoutForm.get('contentCtaUrl')?.value,
          CONTENT_DISCLAIMER: this.sendoutForm.get('contentDisclaimer')?.value,
          CONTENT_HEADLINE: this.sendoutForm.get('contentHeadline')?.value,
          CONTENT_IMAGE_URL: this.sendoutForm.get('contentImageUrl')?.value,
          CONTENT_SUBJECT_LINE:
            this.sendoutForm.get('contentSubjectLine')?.value,
          CONTENT_PREHEADER: this.sendoutForm.get('contentPreheader')?.value,
          POINTS: this.previewPoints,
          CONTENT_STORE_SIGNATURE: this.previewStoreSignature,
        },
      };
      this.emailForm.disable();
      this.mailPreviewService
        .sendMailPreview(this.emailForm.value.email, data)
        .then(() => {
          this.emailForm.enable();
          this.snackbarService.show('Emailen er afsendt', 1200);
        })
        .catch(() => {
          this.emailForm.enable();
          this.snackbarService.show('Emailen blev ikke afsendt', 5000);
        });
    } else if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      this.emailForm.updateValueAndValidity();
    }
  }

  setFormValues(sendout: Sendout): void {
    this.sendoutForm.patchValue({
      storeIds: sendout.storeIds,
      sendoutDate: sendout.sendoutDate,
      sendoutName: sendout.sendoutName,
      contentImageUrl: sendout.contentImageUrl,
      contentSubjectLine: sendout.contentSubjectLine,
      contentPreheader: sendout.contentPreheader,
      contentHeadline: sendout.contentHeadline,
      contentBodyCopy: sendout.contentBodyCopy,
      contentCtaUrl: sendout.contentCtaUrl,
      contentCtaText: sendout.contentCtaText,
      contentDisclaimer: sendout.contentDisclaimer,
    });
  }

  async executeCreateSendout(payload: CreateSendout): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.sendoutService.createSendout(payload)
      );
      this.snackbarService.show('Ændringerne er gemt');
      this.router.navigate(['sendout', res.id]);
    } catch (error) {
      console.error('error :', error);
      this.snackbarService.show('Ændringerne kunne ikke gemmes');
      this.changeDetectorRef.detectChanges();
    }
  }

  async executeUpdateSendout(payload: UpdateSendout): Promise<void> {
    try {
      await firstValueFrom(this.sendoutService.updateSendout(payload));
      this.snackbarService.show('Ændringerne er gemt');
      await this.loadDataForSendoutId(payload.id);
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      console.error('error :', error);
      this.snackbarService.show('Ændringerne kunne ikke gemmes');
      this.changeDetectorRef.detectChanges();
    }
  }

  private getReviewMetadata(
    status: SendoutStatusEnum,
    isReviewAction: boolean
  ): Pick<UpdateSendout, 'reviewedBy' | 'reviewedAt'> {
    if (
      isReviewAction &&
      (status === SendoutStatus.Approved || status === SendoutStatus.Rejected)
    ) {
      return {
        reviewedBy: this.sessionData.userId,
        reviewedAt: formatISO(new Date()),
      };
    }

    return {
      reviewedBy: this.sendout?.reviewedBy || null,
      reviewedAt: this.sendout?.reviewedAt
        ? formatISO(this.sendout.reviewedAt)
        : null,
    };
  }

  /**
   * Update the sendout status and possibly reviewedBy/reviewedAt fields
   * @param status
   * @returns
   */
  async updateSendoutStatus(status: SendoutStatusEnum): Promise<void> {
    const reviewMetadata = this.getReviewMetadata(status, true);
    const payload: UpdateSendout = {
      sendoutType: this.sendout.sendoutType,
      sendoutTemplateId: this.sendout.sendoutTemplateId,
      storeIds: this.sendout.storeIds,
      updatedBy: this.sendout.updatedBy,
      reviewedBy: reviewMetadata.reviewedBy,
      reviewedAt: reviewMetadata.reviewedAt,
      sendoutName: this.sendout.sendoutName,
      status: status,
      contentSubjectLine: this.sendout.contentSubjectLine,
      contentPreheader: this.sendout.contentPreheader,
      contentHeadline: this.sendout.contentHeadline,
      contentBodyCopy: this.sendout.contentBodyCopy,
      contentCtaText: this.sendout.contentCtaText,
      contentCtaUrl: this.sendout.contentCtaUrl,
      contentImageUrl: this.sendout.contentImageUrl,
      contentDisclaimer: this.sendout.contentDisclaimer,
      sendoutDate: formatISO(this.sendout.sendoutDate, {
        representation: 'date',
      }),
      id: this.sendout.id,
      eventId: null,
      contactPerson: '',
    };
    return this.executeUpdateSendout(payload);
  }

  afbrydClick = () => {
    this.router.navigate(['overview']);
  };

  sletUdsendelseClick() {
    this.dialogService.openDialog(
      'Slet udsendelse',
      'Er du sikker på, at du vil slette denne udsendelse?',
      'Slet',
      async () => {
        await this.deleteSendoutById(this.sendout.id).then(() => {
          this.router.navigate(['sendouts']);
        });
      },
      undefined,
      '347px',
      'content-container',
      '',
      '',
      'dialog-actions'
    );
  }

  deleteSendoutById = (id: number) => {
    return new Promise((resolve, reject) => {
      this.sendoutService.deleteSendoutById(id).subscribe({
        next: (data) => {
          this.snackbarService.show('Udsendelsen er slettet');
          this.changeDetectorRef.detectChanges();
          resolve(data);
        },
        error: (error) => {
          console.error('error :', error);
          this.changeDetectorRef.detectChanges();
          reject(error);
        },
        complete: () => {
          this.changeDetectorRef.detectChanges();
        },
      });
    });
  };

  selectStores() {
    const dialogRef = this.dialog.open(StorePickerDialogComponent, {
      width: '90%',
      maxWidth: '100vw',
      data: {
        selectedStores: this.selectedStores,
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && result.selectedStores) {
        this.selectedStores = result.selectedStores;
        this.sendoutForm.patchValue({
          storeIds: this.selectedStores.map((store) => store.id),
        });
        this.changeDetectorRef.detectChanges();
      }
    });
  }
}
